import { useMemo, useState } from 'react';
import { useAuthState } from 'core/providers/AuthProvider';
import { DashboardTab, dashboardTabs } from 'core/constants/dashboard-tab';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import DashboardView from './dashboard-view/dashboard-view';
import DeliveryRequestReport from './delivery-request-report/delivery-request-report';

/**
 * The Dashboard, which now reports on two things.
 *
 * Deliveries — the pharmacy round — is what has always been here. Delivery
 * requests is the inbound courier and transport demand, and where it ends up.
 *
 * Each tab carries the roles that own it and only the tabs a person holds a
 * role for are offered, so a pharmacy dispenser never sees the request numbers
 * and a transport admin never sees the delivery rounds. Somebody who holds
 * only one of them gets no tab bar at all, because a single tab is decoration.
 */
const Dashboard = () => {
  const { userRoles } = useAuthState();

  const tabs = useMemo(
    () => dashboardTabs.filter((tab) => tab.roles.some((role) => userRoles?.includes(role))),
    [userRoles],
  );

  const [selectedTab, setSelectedTab] = useState<string>();
  // Falls back to the first tab the person can see rather than to a fixed one,
  // which for a transport admin would be a screen they have no access to.
  const currentTab = selectedTab ?? tabs[0]?.key;

  return (
    <>
      <PageHeader title='Dashboard' />

      {tabs.length > 1 && (
        <div className='relative mt-4'>
          <div className='relative z-10 flex'>
            {tabs.map((tab) => (
              <SharedTabButton
                key={tab.key}
                changeTab={setSelectedTab}
                tab={tab}
                currentTab={currentTab!}
                testId={`dashboard-tab-${tab.key}`}
              />
            ))}
          </div>
          <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
        </div>
      )}

      <div className='overflow-y-auto'>
        {currentTab === DashboardTab.DELIVERY_REQUESTS && <DeliveryRequestReport />}
        {currentTab === DashboardTab.DELIVERIES && <DashboardView />}
      </div>
    </>
  );
};

export default Dashboard;
